import React, { useState, useEffect } from 'react';
import { 
  FaPlus, FaEdit, FaTrash, FaSearch, FaCalendar, FaUsers, FaBook, FaSpinner, FaArrowLeft,
  FaArrowRight
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { normalizeArray } from '../utils/userUtils';
import { useToast } from '../utils/toastContext';
import { formatApiError } from '../utils/apiErrorFormatter';
import CreateCurriculumModern from './CreateCurriculumModern';
import EditCurriculumModern from './EditCurriculumModern';
import CurriculumFormsPanel from './CurriculumFormsPanel';
import '../styles/ManageCurriculums.css';

const ManageCurriculums = () => {
  const { showSuccess, showError } = useToast();
  const [curricula, setCurricula] = useState([]);
  const [students, setStudents] = useState([]);
  const [preceptors, setPreceptors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [mode, setMode] = useState('list');
  const [selectedCurriculum, setSelectedCurriculum] = useState(null);

  useEffect(() => {
    loadCurricula();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const [studentsRes, preceptorsRes] = await Promise.all([
        axiosInstance.get('/crud/get_all_students'),
        axiosInstance.get('/crud/get_all_preceptors')
      ]);
      setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : []);
      setPreceptors(Array.isArray(preceptorsRes.data) ? preceptorsRes.data : []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const loadCurricula = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/curriculum/curriculums');
      setCurricula(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showError(formatApiError(err, 'Failed to load curricula'));
    } finally {
      setLoading(false);
    }
  };

const handleCreateMode = () => {
    setSelectedCurriculum(null);
    setMode('create');
  };

const handleEditMode = (curriculum) => {
    setSelectedCurriculum(curriculum);
    setMode('edit');
  };

  const handleViewMode = (curriculum) => {
    setSelectedCurriculum(curriculum);
    setMode('view');
  };

  const handleBackToList = () => {
    setMode('list');
  };

const handleDelete = async (curriculumToDelete = selectedCurriculum) => {
    if (!curriculumToDelete?._id) return;
    try {
      await axiosInstance.delete(`/curriculum/curriculum/${curriculumToDelete._id}`);
      showSuccess('Curriculum deleted');
      await loadCurricula();
      if (selectedCurriculum?._id === curriculumToDelete._id) {
        setSelectedCurriculum(null);
      }
      setMode('list');
    } catch (err) {
      showError(formatApiError(err, 'Failed to delete'));
    }
  };

  const getStudentCount = (c) => (c?.student_ids?.length || c?.students?.length || 0);
  const getPreceptorCount = (c) => (c?.preceptor_ids?.length || c?.preceptors?.length || 0);
  const getCurriculumStatus = (curriculum) => {
    const now = new Date();
    const start = new Date(curriculum?.start_date);
    const end = new Date(curriculum?.end_date);
    if (now < start) return { label: 'Upcoming', className: 'is-status-upcoming' };
    if (now > end) return null;
    return { label: 'Active', className: 'is-status-active' };
  };

  const filteredCurricula = curricula.filter((c) => {
    const haystack = `${c?.name || ''} ${c?.rank || ''}`.toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });
  const activeCurriculaCount = curricula.filter((curriculum) => getCurriculumStatus(curriculum)?.label === 'Active').length;

  const stepsByMode = {
    list: ['list'],
    view: ['list', 'view'],
    create: ['list', 'create'],
    edit: ['list', 'edit']
  };
  const labelsByStep = {
    list: 'Curricula',
    view: 'Manage Curriculum',
    create: 'Create Curriculum',
    edit: 'Edit Curriculum'
  };
  const activeSteps = stepsByMode[mode] || ['list'];
  const stepCanGo = (step) => activeSteps.includes(step);
  const goToStep = (step) => {
    if (!stepCanGo(step)) return;
    if (step === 'list') { handleBackToList(); return; }
    if (step === 'view' && selectedCurriculum) { setMode('view'); return; }
    if (step === 'edit' && selectedCurriculum) { setMode('edit'); return; }
    if (step === 'create') { handleCreateMode(); }
  };

  if (loading) {
    return (
      <div className="mc-page" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <FaSpinner className="mc-spin" style={{ fontSize: '3rem', color: '#4a90e2', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div className="mc-page">
      <div className="universal-white-container mc-shell">
      <div className="mc-steps-bar">
        {['list', ...(mode === 'create' ? ['create'] : mode === 'edit' ? ['edit'] : ['view', 'edit'].filter(stepCanGo))].map((step, index, arr) => (
          <React.Fragment key={step}>
            <button
              type="button"
              className={[
                'mc-step',
                mode === step ? 'is-active' : '',
                stepCanGo(step) ? 'is-enabled' : 'is-disabled'
              ].join(' ')}
              onClick={() => goToStep(step)}
            >
              {labelsByStep[step]}
            </button>
            {index < arr.length - 1 && <FaArrowRight className="mc-step-arrow" />}
          </React.Fragment>
        ))}
      </div>

      {(mode === 'list' || mode === 'view') && (
        <div>
          <div className="mc-hero">
            <div className="mc-hero__copy">
              <span className="mc-eyebrow">Admin</span>
              <h1>Manage Curricula</h1>
              <p>Create, manage, and update curricula.</p>
            </div>
            <div className="mc-hero__stats">
              <div className="mc-stat">
                <strong>{curricula.length}</strong>
                <span>Curricula</span>
              </div>
              <div className="mc-stat">
                <strong>{curricula.reduce((s, c) => s + getStudentCount(c), 0)}</strong>
                <span>Students</span>
              </div>
              <div className="mc-stat">
                <strong>{curricula.reduce((s, c) => s + getPreceptorCount(c), 0)}</strong>
                <span>Preceptors</span>
              </div>
              <div className="mc-stat">
                <strong>{activeCurriculaCount}</strong>
                <span>Active Now</span>
              </div>
            </div>
          </div>

          <div className="mc-toolbar">
            <div className="mc-search-box">
              <FaSearch />
              <input
                type="text"
                placeholder="Search by name or rank..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="mc-primary-btn" type="button" onClick={handleCreateMode}>
              <FaPlus /> New Curriculum
            </button>
          </div>

          <div className="mc-cards-grid">
            {filteredCurricula.length === 0 ? (
              <div className="mc-empty-state">
                <FaBook />
                <h3>No curricula yet</h3>
                <p>Create your first curriculum to get started</p>
              </div>
            ) : (
              filteredCurricula.map((curr) => (
                (() => {
                  const status = getCurriculumStatus(curr);
                  return (
                <div
                  key={curr._id}
                  className={[
                    'mc-curriculum-card',
                    status ? `is-${status.label.toLowerCase()}` : 'is-completed-muted',
                    selectedCurriculum?._id === curr._id && mode === 'view' ? 'is-active' : ''
                  ].join(' ')}
                  onClick={() => handleViewMode(curr)}
                >
                  <div className="mc-curriculum-card__top">
                    <div>
                      <h4>{curr.name}</h4>
                      <p className="mc-curriculum-card__rank">{curr.rank}</p>
                    </div>
                    {status && <div className={`mc-status-pill ${status.className}`}>{status.label}</div>}
                  </div>
                  <div className="mc-curriculum-card__meta">
                    <span><FaCalendar /> {new Date(curr.start_date).toLocaleDateString()} → {new Date(curr.end_date).toLocaleDateString()}</span>
                    <span><FaUsers /> {getStudentCount(curr)} students, {getPreceptorCount(curr)} preceptors</span>
                  </div>
                  <div className="mc-card-metrics">
                    <div className="mc-card-metric">
                      <strong>{getStudentCount(curr)}</strong>
                      <small>Students</small>
                    </div>
                    <div className="mc-card-metric">
                      <strong>{getPreceptorCount(curr)}</strong>
                      <small>Preceptors</small>
                    </div>
                    <div className="mc-card-metric">
                      <strong>{Math.max(1, getPreceptorCount(curr))}:{Math.max(1, getStudentCount(curr))}</strong>
                      <small>Ratio</small>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                    <button className="mc-secondary-btn" type="button" onClick={(e) => { e.stopPropagation(); handleEditMode(curr); }}>
                      <FaEdit /> Edit
                    </button>
                    <button className="mc-danger-btn" type="button" onClick={(e) => { e.stopPropagation(); handleDelete(curr); }}>
                      <FaTrash />
                    </button>
                  </div>
                </div>
                  );
                })()
              ))
)}
          </div>

          {mode === 'view' && selectedCurriculum && (
            <div className="mc-editor-panel mc-transition-up" style={{ marginTop: '1.25rem' }}>
              <div className="mc-editor-head">
                <div>
                  <h3>📘 {selectedCurriculum.name}</h3>
                  <p>Manage this curriculum without leaving the page</p>
                </div>
                <div className="mc-editor-actions">
                  <button className="mc-secondary-btn is-on-dark" type="button" onClick={handleBackToList}>
                    <FaArrowLeft /> Back
                  </button>
                  <button className="mc-primary-btn" type="button" onClick={() => handleEditMode(selectedCurriculum)}>
                    <FaEdit /> Manage / Edit
                  </button>
                  <button className="mc-danger-btn" type="button" onClick={() => handleDelete(selectedCurriculum)}>
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>

              <div className="mc-grid-2">
                <div className="mc-form-card">
                  <h4 className="mc-subcard-title">Details</h4>
                  <div className="mc-overview-list">
                    <div>
                      <strong>Rank</strong>
                      <span>{selectedCurriculum.rank || 'N/A'}</span>
                    </div>
                    <div>
                      <strong>Period</strong>
                      <span>
                        {new Date(selectedCurriculum.start_date).toLocaleDateString()} - {new Date(selectedCurriculum.end_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mc-form-card">
                  <h4 className="mc-subcard-title">Assignments</h4>
                  <div className="mc-overview-list">
                    <div>
                      <strong>Students</strong>
                      <span>{getStudentCount(selectedCurriculum)}</span>
                    </div>
                    <div>
                      <strong>Preceptors</strong>
                      <span>{getPreceptorCount(selectedCurriculum)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attach form templates to this existing curriculum, with a schedule
              and audience — the create-curriculum flow could already do this,
              but there was no way to reach it once a curriculum existed. */}
          {mode === 'view' && selectedCurriculum && (
            <CurriculumFormsPanel
              key={selectedCurriculum._id}
              curriculumId={selectedCurriculum._id}
              curriculumName={selectedCurriculum.name}
            />
          )}
        </div>
      )}

      {mode === 'create' && (
        <CreateCurriculumModern
          embedded
          isOpen
          onNotifySuccess={showSuccess}
          onNotifyError={showError}
          onSuccess={async () => {
            await loadCurricula();
            setMode('list');
          }}
          onCancel={handleBackToList}
        />
      )}

      {mode === 'edit' && selectedCurriculum && (
        <EditCurriculumModern
          curriculum={selectedCurriculum}
          onSuccess={async () => {
            await loadCurricula();
            setMode('list');
          }}
          onCancel={handleBackToList}
          onNotifySuccess={showSuccess}
          onNotifyError={showError}
        />
      )}

      <style>
        {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
      </style>
      </div>
    </div>
  );
};

export default ManageCurriculums;
